import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b7533cee-6cc1-4a41-9e88-4b20184fec89/artifacts/eixt3fs2_STM%20mobile%20logo.png';

const Terms = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B0F1A' }}>
      <SEOHead
        title="Terms of Service | ScopeTheMarket"
        description="ScopeTheMarket terms of service. Read our terms and conditions for using our S&P 500 stock scanner and market intelligence platform."
        canonical="https://scopethemarket.com/terms"
        noIndex={false}
      />
      {/* Navigation */}
      <nav className="relative z-50 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="ScopeTheMarket" 
                className="h-9 w-auto"
                style={{ filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.5))' }}
              />
              <span className="text-xl font-semibold tracking-tight" style={{ color: '#F8FAFC' }}>
                ScopeTheMarket
              </span>
            </Link>
            
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-medium transition-colors hover:text-white"
                style={{ color: '#94A3B8' }}
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                className="px-5 py-2.5 text-sm font-medium rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  color: 'white'
                }}
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 py-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link 
            to="/"
            className="inline-flex items-center gap-2 mb-8 text-sm transition-colors hover:text-white"
            style={{ color: '#64748B' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <h1 
            className="text-4xl font-bold mb-4"
            style={{ color: '#F8FAFC' }}
          >
            Terms of Service
          </h1>
          <p className="mb-12" style={{ color: '#64748B' }}>
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          {/* Content */}
          <div className="space-y-10" style={{ color: '#94A3B8' }}>
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>1. Acceptance of Terms</h2>
              <p className="leading-relaxed">
                By accessing and using ScopeTheMarket ("the Service"), you agree to be bound by these Terms of Service. 
                If you do not agree to these terms, please do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>2. Description of Service</h2>
              <p className="leading-relaxed">
                ScopeTheMarket provides AI-powered market anomaly detection and analysis tools. The Service is designed 
                to help users identify unusual market activity and understand potential factors driving market movements.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>3. Not Financial Advice</h2>
              <p className="leading-relaxed">
                The information provided by ScopeTheMarket is for informational purposes only and should not be 
                construed as financial advice. We do not recommend buying, selling, or holding any securities. Always 
                conduct your own research and consult with qualified financial professionals before making investment decisions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>4. User Accounts</h2>
              <p className="leading-relaxed mb-4">
                To access certain features of the Service, you must create an account. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate and complete information during registration</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access to your account</li>
                <li>Accept responsibility for all activities that occur under your account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>5. Subscription and Billing</h2>
              <p className="leading-relaxed mb-4">
                Some features of the Service require a paid subscription. By subscribing, you agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Pay all applicable fees as described at the time of purchase</li>
                <li>Automatic renewal of your subscription unless canceled before the renewal date</li>
                <li>Refund policies as outlined in our billing documentation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>6. Acceptable Use</h2>
              <p className="leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use the Service for any illegal purposes</li>
                <li>Attempt to gain unauthorized access to any part of the Service</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Scrape, copy, or redistribute content without permission</li>
                <li>Share your account credentials with third parties</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>7. Intellectual Property</h2>
              <p className="leading-relaxed">
                All content, features, and functionality of the Service are owned by ScopeTheMarket and are 
                protected by intellectual property laws. You may not reproduce, distribute, or create derivative 
                works without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>8. Limitation of Liability</h2>
              <p className="leading-relaxed">
                To the maximum extent permitted by law, ScopeTheMarket shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages, or any loss of profits or revenues, 
                whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>9. Changes to Terms</h2>
              <p className="leading-relaxed">
                We reserve the right to modify these terms at any time. We will notify users of significant changes 
                by posting a notice on the Service. Your continued use of the Service after such modifications 
                constitutes your acceptance of the updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>10. Contact</h2>
              <p className="leading-relaxed">
                If you have any questions about these Terms of Service, please contact us through our support channels.
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-12 px-6" style={{ borderTop: '1px solid rgba(148, 163, 184, 0.1)' }}>
        <div className="max-w-6xl mx-auto">
          <p className="text-sm text-center" style={{ color: '#475569' }}>
            © {new Date().getFullYear()} ScopeTheMarket. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
